import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Typography,
  CircularProgress,
  makeStyles,
  withStyles,
  Divider,
} from "@material-ui/core";
import axios from "axios";
import { Check } from "@material-ui/icons";
import { useApiBaseUrl } from "../../../CommonMethods/CommonMethods";
import { appUrl } from "../../../../../../../utils/config";

const useStyles = makeStyles((theme) => ({
  table: {
    minWidth: 1200,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& td, & th": {
      borderRight: "1px solid #e0e0e0",
      padding: "8px 16px !important",
    },
    "& td:last-child, & th:last-child": {
      borderRight: "none",
    },
  },
  tableHead: {
    backgroundColor: "#f9f9fc",
  },
  tableCell: {
    fontSize: 16,
  },
  iosSwitch: {
    "& > *": {
      height: "22px !important",
    },
    "& .MuiSwitch-switchBase .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "& .MuiSwitch-switchBase": {
      left: "2px",
      top: "-1px",
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      left: "auto",
      right: "18px",
    },
    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-track": {
      backgroundColor: "#1890ff !important",
    },
  },
  checkedSwitch: {
    position: "absolute",
    width: "16px !important",
    left: "12px",
    top: "-3px",
    color: "white",
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  actionButtons: {
    display: "flex",
    flexDirection: "column",
    marginTop: 4,
  },
  mandatoryDropdown: {
    width: "100%", // This will make it take full available width
    "& .MuiSelect-root": {
      fontSize: "14px",
      padding: "8px 12px",
      textAlign: "left",
    },
    "& .MuiSelect-icon": {
      right: "8px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c0c0c0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#a0a0a0",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1890ff",
    },
    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
      borderColor: "#c0c0c0 !important", // keep border visible
    },
  },
}));

const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 44,
    height: 22,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 26 / 2,
    backgroundColor: "#c6c6c6",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    disabled={props.disabled}
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

const ProductsTypesSection = forwardRef(
  (
    {
      productTypesData: initialProductsTypesData = [],
      isProductsLoading: initialProductsTypesLoading = false,
      editMode: initialEditMode = false,
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
      onChange,
    },
    ref
  ) => {
    const classes = useStyles();
    const [productTypesData, setProductTypesData] = useState([
      ...initialProductsTypesData,
    ]);
    const [isProductsLoading, setIsProdLoading] = useState(
      initialProductsTypesLoading
    );
    const [editMode, setEditMode] = useState(initialEditMode);
    const [isModified, setIsModified] = useState(false);
    const [tableErrors, setTableErrors] = useState({});
    const [originalProductsTypesData, setOriginalProductsTypesData] = useState([
      ...initialProductsTypesData,
    ]);
    const [hasFetched, setHasFetched] = useState(false);
    const API_BASE_URL = useApiBaseUrl(appUrl);
    const token = localStorage.getItem("authToken");
    // Update state when props change - only before fetch completes
    // After fetch, component manages its own state to prevent overwrites
    useEffect(() => {
      if (!hasFetched) {
        setProductTypesData([...initialProductsTypesData]);
        setOriginalProductsTypesData([...initialProductsTypesData]);
        setIsProdLoading(initialProductsTypesLoading);
        setEditMode(initialEditMode);
        setIsModified(false);
      }
    }, [
      initialProductsTypesData,
      initialProductsTypesLoading,
      initialEditMode,
      hasFetched,
    ]);

    // Check if data has been modified
    useEffect(() => {
      const hasChanged =
        JSON.stringify(productTypesData) !==
        JSON.stringify(originalProductsTypesData);
      if (isModified !== hasChanged) {
        setIsModified(hasChanged);
      }
    }, [productTypesData, originalProductsTypesData, isModified]);

    // Fetch document types when entering edit mode
    useEffect(() => {
      let isMounted = true;
      const fetchDocumentTypes = async () => {
        // Only fetch once on initial mount to prevent overwriting user changes
        if (!isProductsLoading && !hasFetched) {
          setIsProdLoading(true);
          try {
            const productConfig = true;
            const response = await axios.get(
              `${API_BASE_URL}/fetchProductTypes/${productConfig}`,
            );
            if (response.data.data.productTypeList && isMounted) {
              const fetchedData = response.data.data.productTypeList.map(
                (doc) => ({
                  productTypeID: doc.productTypeID,
                  productTypeName: doc.productTypeName,
                  activated: true,
                  consumptionDataReqdForIBDGRN: [1, 2].includes(
                    doc.productTypeID,
                  )
                    ? false
                    : doc.productType === "Default"
                      ? true
                      : false,
                      // : "NA",
                  uploadConsumptionData:
                    ![1, 2].includes(doc.productTypeID) === true &&
                    doc.productType === "Default"
                      ? true
                      : false,
                      // : "NA",
                      consumptionSameAsAllocation: false,
                  manuallyAddConsumptionData: false,
                  expiryDateProvided: true,
                  consumedQtyInfoPresent: null,
                  consumedQtyMappedTo: null,
                  // consumptionSameAsAllocation: "NA",
                  // manuallyAddConsumptionData: "NA",
                  earlyDateVarianceLimit: null,
                  earlyPODateUpdateInERP: null,
                  earlyDateUpdateType: null,
                  earlyDateWarningMessageRequired: null,
                  laterDateVarianceLimit: null,
                  laterPODateUpdateInERP: null,
                  laterDateUpdateType: null,
                  laterDateWarningMessageRequired: null,
                  consumedQtyVarianceLimitPercentage: null,
                  consumedQtyVarianceApprovalRequired: null,
                  consumedQtyVarianceWarningMessageRequired: null,
                  lotMismatchApprovalRequired: null,
                  lotMismatchWarningMessageRequired: null,
                  lessProducedQtyVarianceLimitPercentage: null,
                  lessProducedQtyUpdateInERP: null,
                  lessProducedQtyUpdateType: null,
                  lessProducedQtyWarningMessageRequired: null,
                  moreProducedQtyVarianceLimitPercentage: null,
                  moreProducedQtyUpdateInERP: null,
                  moreProducedQtyUpdateType: null,
                  moreProducedQtyWarningMessageRequired: null,
                }),
              );

              // Merge fetched data with existing data by productTypeID
              // START WITH FETCHED DATA to ensure all 7 products are included
              const mergedData = fetchedData.map((fetchedDoc) => {
                const existingDoc = productTypesData.find(
                  (existing) =>
                    existing.productTypeID === fetchedDoc.productTypeID,
                );

                if (existingDoc) {
                  // Product exists in vendorProductTypeConfigs: use its configured state
                  return {
                    ...fetchedDoc,
                    productTypeName: fetchedDoc.productTypeName,
                    activated: existingDoc.activated,
                    // BCM-891: absent on a CMO saved before these existed, so
                    // they read as No rather than an empty dropdown.
                    documentIDReqd:
                      typeof existingDoc.documentIDReqd === "boolean"
                        ? existingDoc.documentIDReqd
                        : false,
                    artworkRequired:
                      typeof existingDoc.artworkRequired === "boolean"
                        ? existingDoc.artworkRequired
                        : false,
                    allocationApprovalReqd:
                      typeof existingDoc.allocationApprovalReqd === "boolean"
                        ? existingDoc.allocationApprovalReqd
                        : false,
                    consumptionDataReqdForIBDGRN:
                      typeof existingDoc.consumptionDataReqdForIBDGRN ===
                      "boolean"
                        ? existingDoc.consumptionDataReqdForIBDGRN
                        : false,
                        // : "NA",
                    uploadConsumptionData:
                      typeof existingDoc.uploadConsumptionData === "boolean"
                        ? existingDoc.uploadConsumptionData
                        : false,
                        // : "NA",
                    consumptionSameAsAllocation:
                      typeof existingDoc.consumptionSameAsAllocation ===
                      "boolean"
                        ? existingDoc.consumptionSameAsAllocation
                        : false,
                        // : "NA",
                    manuallyAddConsumptionData:
                      typeof existingDoc.manuallyAddConsumptionData ===
                      "boolean"
                        ? existingDoc.manuallyAddConsumptionData
                        : false,
                        // : "NA",
                    consumedQtyInfoPresent:
                      existingDoc.consumedQtyInfoPresent === true || existingDoc.consumedQtyInfoPresent === false
                        ? existingDoc.consumedQtyInfoPresent
                        : null,
                    consumedQtyMappedTo:
                      ["onHandQty", "bomQty"].includes(existingDoc.consumedQtyMappedTo)
                        ? existingDoc.consumedQtyMappedTo
                        : null,
                    expiryDateProvided:
                      typeof existingDoc.expiryDateProvided === "boolean"
                        ? existingDoc.expiryDateProvided
                        : true,
                    earlyDateVarianceLimit: existingDoc.earlyDateVarianceLimit ?? null,
                    earlyPODateUpdateInERP: typeof existingDoc.earlyPODateUpdateInERP === "boolean" ? existingDoc.earlyPODateUpdateInERP : null,
                    earlyDateUpdateType: existingDoc.earlyDateUpdateType || null,
                    earlyDateWarningMessageRequired: typeof existingDoc.earlyDateWarningMessageRequired === "boolean" ? existingDoc.earlyDateWarningMessageRequired : null,
                    laterDateVarianceLimit: existingDoc.laterDateVarianceLimit ?? null,
                    laterPODateUpdateInERP: typeof existingDoc.laterPODateUpdateInERP === "boolean" ? existingDoc.laterPODateUpdateInERP : null,
                    laterDateUpdateType: existingDoc.laterDateUpdateType || null,
                    laterDateWarningMessageRequired: typeof existingDoc.laterDateWarningMessageRequired === "boolean" ? existingDoc.laterDateWarningMessageRequired : null,
                    consumedQtyVarianceLimitPercentage: existingDoc.consumedQtyVarianceLimitPercentage ?? null,
                    consumedQtyVarianceApprovalRequired: typeof existingDoc.consumedQtyVarianceApprovalRequired === "boolean" ? existingDoc.consumedQtyVarianceApprovalRequired : null,
                    consumedQtyVarianceWarningMessageRequired: typeof existingDoc.consumedQtyVarianceWarningMessageRequired === "boolean" ? existingDoc.consumedQtyVarianceWarningMessageRequired : null,
                    lotMismatchApprovalRequired: typeof existingDoc.lotMismatchApprovalRequired === "boolean" ? existingDoc.lotMismatchApprovalRequired : null,
                    lotMismatchWarningMessageRequired: typeof existingDoc.lotMismatchWarningMessageRequired === "boolean" ? existingDoc.lotMismatchWarningMessageRequired : null,
                    lessProducedQtyVarianceLimitPercentage: existingDoc.lessProducedQtyVarianceLimitPercentage ?? null,
                    lessProducedQtyUpdateInERP: typeof existingDoc.lessProducedQtyUpdateInERP === "boolean" ? existingDoc.lessProducedQtyUpdateInERP : null,
                    lessProducedQtyUpdateType: existingDoc.lessProducedQtyUpdateType || null,
                    lessProducedQtyWarningMessageRequired: typeof existingDoc.lessProducedQtyWarningMessageRequired === "boolean" ? existingDoc.lessProducedQtyWarningMessageRequired : null,
                    moreProducedQtyVarianceLimitPercentage: existingDoc.moreProducedQtyVarianceLimitPercentage ?? null,
                    moreProducedQtyUpdateInERP: typeof existingDoc.moreProducedQtyUpdateInERP === "boolean" ? existingDoc.moreProducedQtyUpdateInERP : null,
                    moreProducedQtyUpdateType: existingDoc.moreProducedQtyUpdateType || null,
                    moreProducedQtyWarningMessageRequired: typeof existingDoc.moreProducedQtyWarningMessageRequired === "boolean" ? existingDoc.moreProducedQtyWarningMessageRequired : null,
                  };
                }

                // Product NOT in vendorProductTypeConfigs: disable it (not activated)
                return {
                  ...fetchedDoc,
                  activated: false,  // ← Products not in viewCMODetails start as inactive
                  consumptionDataReqdForIBDGRN: false,
                  uploadConsumptionData: false,
                  consumptionSameAsAllocation: false,
                  manuallyAddConsumptionData: false,
                  consumedQtyInfoPresent: null,
                  consumedQtyMappedTo: null,
                  earlyDateVarianceLimit: null,
                  earlyPODateUpdateInERP: null,
                  earlyDateUpdateType: null,
                  earlyDateWarningMessageRequired: null,
                  laterDateVarianceLimit: null,
                  laterPODateUpdateInERP: null,
                  laterDateUpdateType: null,
                  laterDateWarningMessageRequired: null,
                  consumedQtyVarianceLimitPercentage: null,
                  consumedQtyVarianceApprovalRequired: null,
                  consumedQtyVarianceWarningMessageRequired: null,
                  lotMismatchApprovalRequired: null,
                  lotMismatchWarningMessageRequired: null,
                  lessProducedQtyVarianceLimitPercentage: null,
                  lessProducedQtyUpdateInERP: null,
                  lessProducedQtyUpdateType: null,
                  lessProducedQtyWarningMessageRequired: null,
                  moreProducedQtyVarianceLimitPercentage: null,
                  moreProducedQtyUpdateInERP: null,
                  moreProducedQtyUpdateType: null,
                  moreProducedQtyWarningMessageRequired: null,
                };
              });

              // originalMergedData preserves raw server values (including null) for
              // accurate change detection — prevents null → true conversion from
              // masking un-saved expiryDateProvided values
              const originalMergedData = mergedData.map((mergedDoc) => {
                const existingDoc = productTypesData.find(
                  (doc) => doc.productTypeID === mergedDoc.productTypeID,
                );
                if (existingDoc) {
                  return {
                    ...mergedDoc,
                    expiryDateProvided: existingDoc.expiryDateProvided,
                  };
                }
                return mergedDoc;
              });

              if (!editMode || !isModified) {
                // Initial load or read-only mode: populate both current and baseline
                setProductTypesData(mergedData);
                setOriginalProductsTypesData(originalMergedData);
              } else {
                // Edit mode with user changes: only add new products, preserve baseline
                const existingIds = productTypesData.map(
                  (doc) => doc.productTypeID,
                );
                const newProducts = fetchedData.filter(
                  (fetchedDoc) =>
                    !existingIds.includes(fetchedDoc.productTypeID),
                );

                if (newProducts.length > 0) {
                  setProductTypesData((prev) => [...prev, ...newProducts]);
                }
                // originalProductsTypesData is intentionally NOT updated to preserve baseline
              }
              setHasFetched(true);
            }
          } catch (error) {
            console.error("Error fetching document types:", error);
          } finally {
            if (isMounted) setIsProdLoading(false);
          }
        }
      };

      fetchDocumentTypes();
      return () => {
        isMounted = false;
      };
    }, [editMode, API_BASE_URL, token]);

    // Expose methods to parent component
    useImperativeHandle(ref, () => ({
      getFormData: () => {
        return productTypesData;
      },
      getPayload: () => {
        const payload = {
          vendorMosymphonyID,
          brandOwnerMosymphonyID,
          vendorPlantDetails: {
            vendorProductTypeConfigs: productTypesData.map((doc) => ({
              productTypeID: doc.productTypeID,
              // productTypeName:
              //   doc.productTypeName?.trim() || doc.productTypeName,
              activated: doc.activated,
              // BCM-891: batch allocation PDF configurations. Defaulted to false
              // so a CMO configured before these existed saves a definite value
              // rather than undefined.
              documentIDReqd:
                typeof doc.documentIDReqd === "boolean" ? doc.documentIDReqd : false,
              artworkRequired:
                typeof doc.artworkRequired === "boolean" ? doc.artworkRequired : false,
              allocationApprovalReqd:
                typeof doc.allocationApprovalReqd === "boolean"
                  ? doc.allocationApprovalReqd
                  : false,
              // Derived, never user-editable — kept in sync with artworkRequired
              // here so the stored value can never drift from what is displayed.
              allocationPDFTitle:
                doc.artworkRequired === true
                  ? "Allocation and Artwork Memo"
                  : "Allocation Memo",
              consumptionDataReqdForIBDGRN:
                typeof doc.consumptionDataReqdForIBDGRN === "boolean"
                  ? doc.consumptionDataReqdForIBDGRN
                  : false,
              uploadConsumptionData:
                typeof doc.uploadConsumptionData === "boolean"
                  ? doc.uploadConsumptionData
                  : false,
              consumptionSameAsAllocation:
                typeof doc.consumptionSameAsAllocation === "boolean"
                  ? doc.consumptionSameAsAllocation
                  : false,
              manuallyAddConsumptionData:
                typeof doc.manuallyAddConsumptionData === "boolean"
                  ? doc.manuallyAddConsumptionData
                  : false,
              consumedQtyInfoPresent:
                doc.consumedQtyInfoPresent === true || doc.consumedQtyInfoPresent === false
                  ? doc.consumedQtyInfoPresent
                  : null,
              consumedQtyMappedTo:
                ["onHandQty", "bomQty"].includes(doc.consumedQtyMappedTo)
                  ? doc.consumedQtyMappedTo
                  : null,
              expiryDateProvided:
                typeof doc.expiryDateProvided === "boolean"
                  ? doc.expiryDateProvided
                  : true,
              earlyDateVarianceLimit: parseInt(doc.earlyDateVarianceLimit),
              earlyPODateUpdateInERP: doc.earlyPODateUpdateInERP,
              earlyDateUpdateType: doc.earlyDateUpdateType || null,
              earlyDateWarningMessageRequired: doc.earlyDateWarningMessageRequired,
              laterDateVarianceLimit: parseInt(doc.laterDateVarianceLimit),
              laterPODateUpdateInERP: doc.laterPODateUpdateInERP,
              laterDateUpdateType: doc.laterDateUpdateType || null,
              laterDateWarningMessageRequired: doc.laterDateWarningMessageRequired,
              consumedQtyVarianceLimitPercentage: parseInt(doc.consumedQtyVarianceLimitPercentage),
              consumedQtyVarianceApprovalRequired: doc.consumedQtyVarianceApprovalRequired,
              consumedQtyVarianceWarningMessageRequired: doc.consumedQtyVarianceWarningMessageRequired,
              lotMismatchApprovalRequired: doc.lotMismatchApprovalRequired,
              lotMismatchWarningMessageRequired: doc.lotMismatchWarningMessageRequired,
              lessProducedQtyVarianceLimitPercentage: parseInt(doc.lessProducedQtyVarianceLimitPercentage),
              lessProducedQtyUpdateInERP: doc.lessProducedQtyUpdateInERP,
              lessProducedQtyUpdateType: doc.lessProducedQtyUpdateType || null,
              lessProducedQtyWarningMessageRequired: doc.lessProducedQtyWarningMessageRequired,
              moreProducedQtyVarianceLimitPercentage: parseInt(doc.moreProducedQtyVarianceLimitPercentage),
              moreProducedQtyUpdateInERP: doc.moreProducedQtyUpdateInERP,
              moreProducedQtyUpdateType: doc.moreProducedQtyUpdateType || null,
              moreProducedQtyWarningMessageRequired: doc.moreProducedQtyWarningMessageRequired,
            })),
          },
          updateVendorProductTypeConfig: true,
        };

        return payload;
      },
      validateForm: () => {
        const activatedDocs = productTypesData.filter((doc) => !!doc.activated);
        const tableChecks = [
          {
            key: "earlyStatTable",
            limitField: "earlyDateVarianceLimit",
            limitLabel: "Variance Limit (days)",
            updateInERPField: "earlyPODateUpdateInERP",
            updateTypeField: "earlyDateUpdateType",
            updateTypeLabel: "Update Type",
            booleanFields: [
              { field: "earlyPODateUpdateInERP", label: "Update PO Stat and Delivery Date in ERP" },
              { field: "earlyDateWarningMessageRequired", label: "Warning Message" },
            ],
          },
          {
            key: "laterStatTable",
            limitField: "laterDateVarianceLimit",
            limitLabel: "Variance Limit (days)",
            updateInERPField: "laterPODateUpdateInERP",
            updateTypeField: "laterDateUpdateType",
            updateTypeLabel: "Update Type",
            booleanFields: [
              { field: "laterPODateUpdateInERP", label: "Update PO Stat and Delivery Date in ERP" },
              { field: "laterDateWarningMessageRequired", label: "Warning Message" },
            ],
          },
          {
            key: "consumedBatchTable",
            limitField: "consumedQtyVarianceLimitPercentage",
            limitLabel: "Qty Variance Limit (±%)",
            updateInERPField: null,
            updateTypeField: null,
            updateTypeLabel: null,
            booleanFields: [
              { field: "consumedQtyVarianceApprovalRequired", label: "Approval Required" },
              { field: "consumedQtyVarianceWarningMessageRequired", label: "Warning Message" },
              { field: "lotMismatchApprovalRequired", label: "Batch Mismatch - Approval Required" },
              { field: "lotMismatchWarningMessageRequired", label: "Batch Mismatch - Warning Message" },
            ],
          },
          {
            key: "lessProducedTable",
            limitField: "lessProducedQtyVarianceLimitPercentage",
            limitLabel: "Variance Limit (%)",
            updateInERPField: "lessProducedQtyUpdateInERP",
            updateTypeField: "lessProducedQtyUpdateType",
            updateTypeLabel: "Update Type",
            booleanFields: [
              { field: "lessProducedQtyUpdateInERP", label: "Update PO Qty in ERP" },
              { field: "lessProducedQtyWarningMessageRequired", label: "Warning Message" },
            ],
          },
          {
            key: "moreProducedTable",
            limitField: "moreProducedQtyVarianceLimitPercentage",
            limitLabel: "Variance Limit (%)",
            updateInERPField: "moreProducedQtyUpdateInERP",
            updateTypeField: "moreProducedQtyUpdateType",
            updateTypeLabel: "Update Type",
            booleanFields: [
              { field: "moreProducedQtyUpdateInERP", label: "Update PO Qty in ERP" },
              { field: "moreProducedQtyWarningMessageRequired", label: "Warning Message" },
            ],
          },
        ];
        const errors = {};
        let isValid = true;
        for (const check of tableChecks) {
          const missingFields = [];
          const hasInvalidLimit = activatedDocs.some((d) => { const v = d[check.limitField]; return v === undefined || v === null || String(v).trim() === "" || isNaN(parseInt(v)); });
          if (hasInvalidLimit) missingFields.push(check.limitLabel);
          if (check.updateInERPField) {
            const hasInvalidType = activatedDocs.some((d) => d[check.updateInERPField] && !d[check.updateTypeField]);
            if (hasInvalidType) missingFields.push(check.updateTypeLabel);
          }
          for (const bf of (check.booleanFields || [])) {
            const hasInvalidBool = activatedDocs.some((d) => d[bf.field] === null || d[bf.field] === undefined);
            if (hasInvalidBool) missingFields.push(bf.label);
          }
          if (missingFields.length) { errors[check.key] = `${missingFields.join(", ")} are mandatory fields`; isValid = false; }
        }
        setTableErrors(errors);
        return isValid;
      },
      resetForm: () => {
        setProductTypesData([...originalProductsTypesData]);
        setIsModified(false);
      },
    }));

    const handleToggle = (id, checked) => {
      setProductTypesData((prev) =>
        prev.map((doc) =>
          doc.productTypeID === id ? { ...doc, activated: checked } : doc,
        ),
      );

      // Notify parent about change
      if (onChange) {
        onChange();
      }
    };

    const handleMandatoryChange = (id, name, value) => {
      setProductTypesData((prev) =>
        prev.map((doc) => {
          if (doc.productTypeID !== id) return doc;
          if (name === "consumptionDataReqdForIBDGRN" && value === "No") {
            return {
              ...doc,
              consumptionDataReqdForIBDGRN: false,
              uploadConsumptionData: false,
              consumptionSameAsAllocation: false,
              consumedQtyInfoPresent: null,
              consumedQtyMappedTo: null,
            };
          }
          if (name === "uploadConsumptionData" && value === "No") {
            return {
              ...doc,
              uploadConsumptionData: false,
              consumedQtyInfoPresent: null,
              consumedQtyMappedTo: null,
            };
          }
          if (name === "consumedQtyInfoPresent") {
            const newVal = value === "Yes" ? true : value === "No" ? false : null;
            return {
              ...doc,
              consumedQtyInfoPresent: newVal,
              consumedQtyMappedTo: newVal === true ? null : doc.consumedQtyMappedTo,
            };
          }
          if (name === "consumedQtyMappedTo") {
            const newVal = value === "BoM Qty" ? "bomQty" : value === "On-Hand Qty" ? "onHandQty" : null;
            return { ...doc, consumedQtyMappedTo: newVal };
          }
          if (name === "earlyPODateUpdateInERP") {
            return { ...doc, earlyPODateUpdateInERP: value === "Yes", earlyDateUpdateType: value === "No" ? null : (doc.earlyDateUpdateType || "Manual") };
          }
          if (name === "laterPODateUpdateInERP") {
            return { ...doc, laterPODateUpdateInERP: value === "Yes", laterDateUpdateType: value === "No" ? null : (doc.laterDateUpdateType || "Manual") };
          }
          if (name === "lessProducedQtyUpdateInERP") {
            return { ...doc, lessProducedQtyUpdateInERP: value === "Yes", lessProducedQtyUpdateType: value === "No" ? null : (doc.lessProducedQtyUpdateType || "Manual") };
          }
          if (name === "moreProducedQtyUpdateInERP") {
            return { ...doc, moreProducedQtyUpdateInERP: value === "Yes", moreProducedQtyUpdateType: value === "No" ? null : (doc.moreProducedQtyUpdateType || "Manual") };
          }
          const varianceLimitFields = [
            "earlyDateVarianceLimit", "laterDateVarianceLimit",
            "consumedQtyVarianceLimitPercentage",
            "lessProducedQtyVarianceLimitPercentage", "moreProducedQtyVarianceLimitPercentage",
          ];
          if (varianceLimitFields.includes(name)) {
            return { ...doc, [name]: value };
          }
          const updateTypeFields = ["earlyDateUpdateType", "laterDateUpdateType", "lessProducedQtyUpdateType", "moreProducedQtyUpdateType"];
          if (updateTypeFields.includes(name)) {
            return { ...doc, [name]: value === "NA" ? null : value };
          }
          return { ...doc, [name]: value === "Yes" };
        }),
      );

      // Notify parent about change
      if (onChange) {
        onChange();
      }
    };

    // Read-only view
    // if (!editMode) {
    //   return (
    //     <>
    //       <div className="cmo-stepper-wrapper plant-details-step">
    //         <div className="stepper-container">
    //           <form autoComplete="off">
    //             {isProductsLoading ? (
    //               <div className={classes.loaderContainer}>
    //                 <CircularProgress />
    //               </div>
    //             ) : (
    //               <TableContainer
    //                 component={Paper}
    //                 style={{ minWidth: 400, marginTop: 16, marginBottom: 16 }}
    //               >
    //                 <Table>
    //                   <TableHead style={{ backgroundColor: "#f9f9fc" }}>
    //                     <TableRow>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Selection
    //                       </TableCell>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Product Type
    //                       </TableCell>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Consumption info req. For IBD/GRN
    //                       </TableCell>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Consumption data present
    //                       </TableCell>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Consumption details = Allocation details
    //                       </TableCell>
    //                       <TableCell style={{ fontSize: 16 }}>
    //                         Consumption data entered manually
    //                       </TableCell>
    //                     </TableRow>
    //                   </TableHead>
    //                   <TableBody>
    //                     {productTypesData.map((doc) => (
    //                       <TableRow key={doc.productTypeID}>
    //                         <TableCell>
    //                           <span style={{ position: "relative" }}>
    //                             <IOSSwitch
    //                               className={classes.iosSwitch}
    //                               checked={doc.activated === true}
    //                               disabled={true}
    //                               color="primary"
    //                             />
    //                             {doc.activated === true && (
    //                               <Check className={classes.checkedSwitch} />
    //                             )}
    //                           </span>
    //                         </TableCell>
    //                         <TableCell>{doc.productTypeName}</TableCell>
    //                         <TableCell>
    //                           <FormControl
    //                             variant="outlined"
    //                             size="small"
    //                             className={classes.mandatoryDropdown}
    //                           >
    //                             <Select
    //                               value={
    //                                 typeof doc.consumptionDataReqdForIBDGRN === "boolean"
    //                                   ? doc.consumptionDataReqdForIBDGRN
    //                                     ? "Yes"
    //                                     : "No"
    //                                   : "NA"
    //                               }
    //                               disabled={true}
    //                               displayEmpty
    //                             >
    //                               <MenuItem value="NA">NA</MenuItem>
    //                               <MenuItem value="No">No</MenuItem>
    //                               <MenuItem value="Yes">Yes</MenuItem>
    //                             </Select>
    //                           </FormControl>
    //                         </TableCell>
    //                         <TableCell>
    //                           <FormControl
    //                             variant="outlined"
    //                             size="small"
    //                             className={classes.mandatoryDropdown}
    //                           >
    //                             <Select
    //                               value={
    //                                 typeof doc.consumptionDataReqdForIBDGRN === "boolean"
    //                                   ? doc.consumptionDataReqdForIBDGRN
    //                                     ? "Yes"
    //                                     : "No"
    //                                   : "NA"
    //                               }
    //                               disabled={true}
    //                               displayEmpty
    //                             >
    //                               <MenuItem value="NA">NA</MenuItem>
    //                               <MenuItem value="No">No</MenuItem>
    //                               <MenuItem value="Yes">Yes</MenuItem>
    //                             </Select>
    //                           </FormControl>
    //                         </TableCell>

    //                         <TableCell>
    //                           <FormControl
    //                             variant="outlined"
    //                             size="small"
    //                             className={classes.mandatoryDropdown}
    //                           >
    //                             <Select
    //                               value={
    //                                 typeof doc.uploadConsumptionData ===
    //                                 "boolean"
    //                                   ? doc.uploadConsumptionData
    //                                     ? "Yes"
    //                                     : "No"
    //                                   : "NA"
    //                               }
    //                               disabled={true}
    //                               displayEmpty
    //                             >
    //                               <MenuItem value="NA">NA</MenuItem>
    //                               <MenuItem value="No">No</MenuItem>
    //                               <MenuItem value="Yes">Yes</MenuItem>
    //                             </Select>
    //                           </FormControl>
    //                         </TableCell>
    //                         <TableCell>
    //                           <FormControl
    //                             variant="outlined"
    //                             size="small"
    //                             className={classes.mandatoryDropdown}
    //                           >
    //                             <Select
    //                               value={
    //                                 typeof doc.manuallyAddConsumptionData === "boolean"
    //                                   ? doc.manuallyAddConsumptionData
    //                                     ? "Yes"
    //                                     : "No"
    //                                   : "NA"
    //                               }
    //                               disabled={true}
    //                               displayEmpty
    //                             >
    //                               <MenuItem value="NA">NA</MenuItem>
    //                               <MenuItem value="No">No</MenuItem>
    //                               <MenuItem value="Yes">Yes</MenuItem>
    //                             </Select>
    //                           </FormControl>
    //                         </TableCell>
    //                       </TableRow>
    //                     ))}
    //                   </TableBody>
    //                 </Table>
    //               </TableContainer>
    //             )}
    //           </form>
    //           <Divider
    //             style={{
    //               margin: "80px 00px 0px 00px",
    //               backgroundColor: "#707070",
    //               height: 1,
    //             }}
    //           />
    //         </div>
    //       </div>
    //     </>
    //   );
    // }

    return (
      <>
        <div className="cmo-stepper-wrapper plant-details-step">
          <div className="stepper-container">
            <form autoComplete="off">
              {isProductsLoading ? (
                <div className={classes.loaderContainer}>
                  <CircularProgress />
                </div>
              ) : (
                <>
                  {/* Table 1: Product Type Selection */}
                  <div style={{ marginBottom: 60 }}>
                    <div
                      style={{
                         fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontWeight: 800,
            fontSize: 18,
                      }}
                    >
                      Product Type Selection
                    </div>
                    <TableContainer
                      component={Paper}
                      className={classes.table}
                    >
                      <Table>
                        <TableHead className={classes.tableHead}>
                          <TableRow>
                            <TableCell className={classes.tableCell}>
                              Selection
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              Product Type
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              Expiry Date Provided
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productTypesData.map((doc) => (
                            <TableRow key={doc.productTypeID}>
                              <TableCell>
                                <span style={{ position: "relative" }}>
                                  <IOSSwitch
                                    className={classes.iosSwitch}
                                    checked={doc.activated === true}
                                    onChange={(e) =>
                                      handleToggle(
                                        doc.productTypeID,
                                        e.target.checked,
                                      )
                                    }
                                    color="primary"
                                    disabled={!editMode}
                                  />
                                  {doc.activated === true && (
                                    <Check className={classes.checkedSwitch} />
                                  )}
                                </span>
                              </TableCell>
                              <TableCell>{doc.productTypeName}</TableCell>
                              <TableCell>
                                <FormControl
                                  variant="outlined"
                                  size="small"
                                  className={classes.mandatoryDropdown}
                                >
                                  <Select
                                    value={
                                      typeof doc.expiryDateProvided === "boolean"
                                        ? doc.expiryDateProvided
                                          ? "Yes"
                                          : "No"
                                        : "Yes"
                                    }
                                    onChange={(e) =>
                                      handleMandatoryChange(
                                        doc.productTypeID,
                                        "expiryDateProvided",
                                        e.target.value,
                                      )
                                    }
                                    disabled={!editMode}
                                    displayEmpty
                                  >
                                    <MenuItem value="Yes">Yes</MenuItem>
                                    <MenuItem value="No">No</MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>

                  {/* Table 2: Product Type Consumption Configurations */}
                  <div>
                    <div
                      style={{
                         fontFamily: "Roboto, Helvetica, Arial, sans-serif",
            fontWeight: 800,
            fontSize: 18,
                      }}
                    >
                      Product Type Consumption Configurations
                    </div>
                    <TableContainer
                      component={Paper}
                      className={classes.table}
                    >
                      <Table>
                        <TableHead className={classes.tableHead}>
                          <TableRow>
                            <TableCell className={classes.tableCell}>
                              Product Type
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              Consumption info req. For IBD/GRN
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              Consumed batch info present
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              For multiple consumed batches, individual consumed
                              qty info present
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              Map consumed qty to
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              Consumption details = Allocation details
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productTypesData.filter((doc) => doc.activated === true).map((doc) => {
                            const consumptionReqd =
                              typeof doc.consumptionDataReqdForIBDGRN ===
                              "boolean"
                                ? doc.consumptionDataReqdForIBDGRN
                                : false;
                            const batchInfoPresent =
                              typeof doc.uploadConsumptionData === "boolean"
                                ? doc.uploadConsumptionData
                                : false;
                            const indivQtyDisabled =
                              !editMode || !consumptionReqd || !batchInfoPresent;
                            const mapQtyDisabled =
                              !editMode ||
                              !consumptionReqd ||
                              !batchInfoPresent ||
                              doc.consumedQtyInfoPresent !== false;
                            // Separate "not applicable" from "disabled for editing"
                            const allocationNotApplicable = !consumptionReqd || batchInfoPresent;
                            const allocationDisabled = !editMode || allocationNotApplicable;
                            return (
                              <TableRow key={doc.productTypeID}>
                                <TableCell>{doc.productTypeName}</TableCell>
                                <TableCell>
                                  <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.mandatoryDropdown}
                                  >
                                    <Select
                                      value={
                                        consumptionReqd ? "Yes" : "No"
                                      }
                                      onChange={(e) =>
                                        handleMandatoryChange(
                                          doc.productTypeID,
                                          "consumptionDataReqdForIBDGRN",
                                          e.target.value,
                                        )
                                      }
                                      disabled={!editMode}
                                      displayEmpty
                                    >
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.mandatoryDropdown}
                                  >
                                    <Select
                                      value={
                                        !consumptionReqd
                                          ? "NA"
                                          : batchInfoPresent
                                          ? "Yes"
                                          : "No"
                                      }
                                      onChange={(e) =>
                                        handleMandatoryChange(
                                          doc.productTypeID,
                                          "uploadConsumptionData",
                                          e.target.value,
                                        )
                                      }
                                      displayEmpty
                                      disabled={!editMode || !consumptionReqd}
                                    >
                                      {!consumptionReqd && (
                                        <MenuItem value="NA">NA</MenuItem>
                                      )}
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.mandatoryDropdown}
                                  >
                                    <Select
                                      value={
                                        doc.consumedQtyInfoPresent === true
                                          ? "Yes"
                                          : doc.consumedQtyInfoPresent === false
                                          ? "No"
                                          : "NA"
                                      }
                                      onChange={(e) =>
                                        handleMandatoryChange(
                                          doc.productTypeID,
                                          "consumedQtyInfoPresent",
                                          e.target.value,
                                        )
                                      }
                                      disabled={indivQtyDisabled}
                                      displayEmpty
                                    >
                                      <MenuItem value="NA" disabled={!indivQtyDisabled}>NA</MenuItem>
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.mandatoryDropdown}
                                  >
                                    <Select
                                      value={
                                        doc.consumedQtyMappedTo === "onHandQty"
                                          ? "On-Hand Qty"
                                          : doc.consumedQtyMappedTo === "bomQty"
                                          ? "BoM Qty"
                                          : "NA"
                                      }
                                      onChange={(e) =>
                                        handleMandatoryChange(
                                          doc.productTypeID,
                                          "consumedQtyMappedTo",
                                          e.target.value,
                                        )
                                      }
                                      disabled={mapQtyDisabled}
                                      displayEmpty
                                    >
                                      <MenuItem value="NA" disabled={!mapQtyDisabled}>NA</MenuItem>
                                      <MenuItem value="BoM Qty">BoM Qty</MenuItem>
                                      <MenuItem value="On-Hand Qty">
                                        On-Hand Qty
                                      </MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.mandatoryDropdown}
                                  >
                                    <Select
                                      value={
                                        allocationNotApplicable
                                          ? "NA"
                                          : typeof doc.consumptionSameAsAllocation ===
                                            "boolean"
                                          ? doc.consumptionSameAsAllocation
                                            ? "Yes"
                                            : "No"
                                          : "NA"
                                      }
                                      onChange={(e) =>
                                        handleMandatoryChange(
                                          doc.productTypeID,
                                          "consumptionSameAsAllocation",
                                          e.target.value,
                                        )
                                      }
                                      displayEmpty
                                      disabled={allocationDisabled}
                                    >
                                      <MenuItem value="NA" disabled={!allocationNotApplicable}>NA</MenuItem>
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>

                  {/* Table 3: Early Stat and Delivery Date */}
                  <div>
                    <div
                      style={{
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 800,
                        fontSize: 18,
                        marginTop: 24,
                      }}
                    >
                      Early Stat and Delivery Date (Batch Execution Acceptance Limits)
                    </div>
                    <TableContainer component={Paper} className={classes.table}>
                      <Table>
                        <colgroup>
                          <col style={{ width: "22%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "24%" }} />
                          <col style={{ width: "26%" }} />
                          <col style={{ width: "16%" }} />
                        </colgroup>
                        <TableHead className={classes.tableHead}>
                          <TableRow>
                            <TableCell className={classes.tableCell}>Product Type</TableCell>
                            <TableCell className={classes.tableCell}>Variance Limit (days) <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Update PO Stat and Delivery Date in ERP <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Update Type <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Warning Message <span style={{ color: "red" }}>*</span></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productTypesData.filter((doc) => !!doc.activated).map((doc) => {
                            const earlyUpdateTypeDisabled = !editMode || !doc.earlyPODateUpdateInERP;
                            return (
                              <TableRow key={doc.productTypeID}>
                                <TableCell>{doc.productTypeName}</TableCell>
                                <TableCell>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={doc.earlyDateVarianceLimit ?? ""}
                                    onChange={(e) => handleMandatoryChange(doc.productTypeID, "earlyDateVarianceLimit", e.target.value)}
                                    disabled={!editMode}
                                    inputProps={{ min: 0 }}
                                    onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                                    style={{ width: 90 }}
                                    error={!!tableErrors.earlyStatTable && (doc.earlyDateVarianceLimit == null || String(doc.earlyDateVarianceLimit).trim() === "" || isNaN(parseInt(doc.earlyDateVarianceLimit)))}
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.earlyStatTable && doc.earlyPODateUpdateInERP == null}>
                                    <Select
                                      value={doc.earlyPODateUpdateInERP === true ? "Yes" : doc.earlyPODateUpdateInERP === false ? "No" : ""}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "earlyPODateUpdateInERP", e.target.value)}
                                      disabled={!editMode}
                                      displayEmpty
                                    >
                                      {doc.earlyPODateUpdateInERP == null && <MenuItem value="" disabled>-</MenuItem>}
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.earlyStatTable && !!doc.earlyPODateUpdateInERP && !doc.earlyDateUpdateType}>
                                    <Select
                                      value={!doc.earlyPODateUpdateInERP ? "NA" : (doc.earlyDateUpdateType || "")}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "earlyDateUpdateType", e.target.value)}
                                      disabled={earlyUpdateTypeDisabled}
                                      displayEmpty
                                    >
                                      {!doc.earlyPODateUpdateInERP && <MenuItem value="NA">NA</MenuItem>}
                                      <MenuItem value="Manual">Manual</MenuItem>
                                      <MenuItem value="Automatic after approval">Automatic after approval</MenuItem>
                                      <MenuItem value="Automatic without approval">Automatic without approval</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.earlyStatTable && doc.earlyDateWarningMessageRequired == null}>
                                    <Select
                                      value={doc.earlyDateWarningMessageRequired === true ? "Yes" : doc.earlyDateWarningMessageRequired === false ? "No" : ""}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "earlyDateWarningMessageRequired", e.target.value)}
                                      disabled={!editMode}
                                      displayEmpty
                                    >
                                      {doc.earlyDateWarningMessageRequired == null && <MenuItem value="" disabled>-</MenuItem>}
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {tableErrors.earlyStatTable && (
                      <Typography style={{ color: "#f44336", fontSize: 14, marginTop: 8, whiteSpace: "pre-line" }}>{tableErrors.earlyStatTable}</Typography>
                    )}
                  </div>

                  {/* Table 4: Later Stat and Delivery Date */}
                  <div>
                    <div
                      style={{
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 800,
                        fontSize: 18,
                        marginTop: 24,
                      }}
                    >
                      Later Stat and Delivery Date (Batch Execution Acceptance Limits)
                    </div>
                    <TableContainer component={Paper} className={classes.table}>
                      <Table>
                        <colgroup>
                          <col style={{ width: "22%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "24%" }} />
                          <col style={{ width: "26%" }} />
                          <col style={{ width: "16%" }} />
                        </colgroup>
                        <TableHead className={classes.tableHead}>
                          <TableRow>
                            <TableCell className={classes.tableCell}>Product Type</TableCell>
                            <TableCell className={classes.tableCell}>Variance Limit (days) <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Update PO Stat and Delivery Date in ERP <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Update Type <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Warning Message <span style={{ color: "red" }}>*</span></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productTypesData.filter((doc) => !!doc.activated).map((doc) => {
                            const laterUpdateTypeDisabled = !editMode || !doc.laterPODateUpdateInERP;
                            return (
                              <TableRow key={doc.productTypeID}>
                                <TableCell>{doc.productTypeName}</TableCell>
                                <TableCell>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={doc.laterDateVarianceLimit ?? ""}
                                    onChange={(e) => handleMandatoryChange(doc.productTypeID, "laterDateVarianceLimit", e.target.value)}
                                    disabled={!editMode}
                                    inputProps={{ min: 0 }}
                                    onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                                    style={{ width: 90 }}
                                    error={!!tableErrors.laterStatTable && (doc.laterDateVarianceLimit == null || String(doc.laterDateVarianceLimit).trim() === "" || isNaN(parseInt(doc.laterDateVarianceLimit)))}
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.laterStatTable && doc.laterPODateUpdateInERP == null}>
                                    <Select
                                      value={doc.laterPODateUpdateInERP === true ? "Yes" : doc.laterPODateUpdateInERP === false ? "No" : ""}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "laterPODateUpdateInERP", e.target.value)}
                                      disabled={!editMode}
                                      displayEmpty
                                    >
                                      {doc.laterPODateUpdateInERP == null && <MenuItem value="" disabled>-</MenuItem>}
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.laterStatTable && !!doc.laterPODateUpdateInERP && !doc.laterDateUpdateType}>
                                    <Select
                                      value={!doc.laterPODateUpdateInERP ? "NA" : (doc.laterDateUpdateType || "")}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "laterDateUpdateType", e.target.value)}
                                      disabled={laterUpdateTypeDisabled}
                                      displayEmpty
                                    >
                                      {!doc.laterPODateUpdateInERP && <MenuItem value="NA">NA</MenuItem>}
                                      <MenuItem value="Manual">Manual</MenuItem>
                                      <MenuItem value="Automatic after approval">Automatic after approval</MenuItem>
                                      <MenuItem value="Automatic without approval">Automatic without approval</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.laterStatTable && doc.laterDateWarningMessageRequired == null}>
                                    <Select
                                      value={doc.laterDateWarningMessageRequired === true ? "Yes" : doc.laterDateWarningMessageRequired === false ? "No" : ""}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "laterDateWarningMessageRequired", e.target.value)}
                                      disabled={!editMode}
                                      displayEmpty
                                    >
                                      {doc.laterDateWarningMessageRequired == null && <MenuItem value="" disabled>-</MenuItem>}
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {tableErrors.laterStatTable && (
                      <Typography style={{ color: "#f44336", fontSize: 14, marginTop: 8, whiteSpace: "pre-line" }}>{tableErrors.laterStatTable}</Typography>
                    )}
                  </div>

                  {/* Table 5: Consumed Batch */}
                  <div>
                    <div
                      style={{
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 800,
                        fontSize: 18,
                        marginTop: 24,
                      }}
                    >
                      Consumed Batch (Batch Execution Acceptance Limits)
                    </div>
                    <TableContainer component={Paper} className={classes.table}>
                      <Table>
                        <colgroup>
                          <col style={{ width: "18%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "14%" }} />
                          <col style={{ width: "14%" }} />
                          <col style={{ width: "21%" }} />
                          <col style={{ width: "21%" }} />
                        </colgroup>
                        <TableHead className={classes.tableHead}>
                          <TableRow>
                            <TableCell className={classes.tableCell}>Product Type</TableCell>
                            <TableCell className={classes.tableCell}>Qty Variance Limit (±%) <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Approval Required <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Warning Message <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Batch Mismatch - Approval Required <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Batch Mismatch - Warning Message <span style={{ color: "red" }}>*</span></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productTypesData.filter((doc) => !!doc.activated).map((doc) => (
                            <TableRow key={doc.productTypeID}>
                              <TableCell>{doc.productTypeName}</TableCell>
                              <TableCell>
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  type="number"
                                  value={doc.consumedQtyVarianceLimitPercentage ?? ""}
                                  onChange={(e) => handleMandatoryChange(doc.productTypeID, "consumedQtyVarianceLimitPercentage", e.target.value)}
                                  disabled={!editMode}
                                  inputProps={{ min: 0 }}
                                  onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                                  style={{ width: 90 }}
                                  error={!!tableErrors.consumedBatchTable && (doc.consumedQtyVarianceLimitPercentage == null || String(doc.consumedQtyVarianceLimitPercentage).trim() === "" || isNaN(parseInt(doc.consumedQtyVarianceLimitPercentage)))}
                                />
                              </TableCell>
                              <TableCell>
                                <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.consumedBatchTable && doc.consumedQtyVarianceApprovalRequired == null}>
                                  <Select
                                    value={doc.consumedQtyVarianceApprovalRequired === true ? "Yes" : doc.consumedQtyVarianceApprovalRequired === false ? "No" : ""}
                                    onChange={(e) => handleMandatoryChange(doc.productTypeID, "consumedQtyVarianceApprovalRequired", e.target.value)}
                                    disabled={!editMode}
                                    displayEmpty
                                  >
                                    {doc.consumedQtyVarianceApprovalRequired == null && <MenuItem value="" disabled>-</MenuItem>}
                                    <MenuItem value="No">No</MenuItem>
                                    <MenuItem value="Yes">Yes</MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell>
                                <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.consumedBatchTable && doc.consumedQtyVarianceWarningMessageRequired == null}>
                                  <Select
                                    value={doc.consumedQtyVarianceWarningMessageRequired === true ? "Yes" : doc.consumedQtyVarianceWarningMessageRequired === false ? "No" : ""}
                                    onChange={(e) => handleMandatoryChange(doc.productTypeID, "consumedQtyVarianceWarningMessageRequired", e.target.value)}
                                    disabled={!editMode}
                                    displayEmpty
                                  >
                                    {doc.consumedQtyVarianceWarningMessageRequired == null && <MenuItem value="" disabled>-</MenuItem>}
                                    <MenuItem value="No">No</MenuItem>
                                    <MenuItem value="Yes">Yes</MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell>
                                <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.consumedBatchTable && doc.lotMismatchApprovalRequired == null}>
                                  <Select
                                    value={doc.lotMismatchApprovalRequired === true ? "Yes" : doc.lotMismatchApprovalRequired === false ? "No" : ""}
                                    onChange={(e) => handleMandatoryChange(doc.productTypeID, "lotMismatchApprovalRequired", e.target.value)}
                                    disabled={!editMode}
                                    displayEmpty
                                  >
                                    {doc.lotMismatchApprovalRequired == null && <MenuItem value="" disabled>-</MenuItem>}
                                    <MenuItem value="No">No</MenuItem>
                                    <MenuItem value="Yes">Yes</MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                              <TableCell>
                                <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.consumedBatchTable && doc.lotMismatchWarningMessageRequired == null}>
                                  <Select
                                    value={doc.lotMismatchWarningMessageRequired === true ? "Yes" : doc.lotMismatchWarningMessageRequired === false ? "No" : ""}
                                    onChange={(e) => handleMandatoryChange(doc.productTypeID, "lotMismatchWarningMessageRequired", e.target.value)}
                                    disabled={!editMode}
                                    displayEmpty
                                  >
                                    {doc.lotMismatchWarningMessageRequired == null && <MenuItem value="" disabled>-</MenuItem>}
                                    <MenuItem value="No">No</MenuItem>
                                    <MenuItem value="Yes">Yes</MenuItem>
                                  </Select>
                                </FormControl>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {tableErrors.consumedBatchTable && (
                      <Typography style={{ color: "#f44336", fontSize: 14, marginTop: 8, whiteSpace: "pre-line" }}>{tableErrors.consumedBatchTable}</Typography>
                    )}
                  </div>

                  {/* Table 6: Less Produced Qty Variance */}
                  <div>
                    <div
                      style={{
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 800,
                        fontSize: 18,
                        marginTop: 24,
                      }}
                    >
                      Less Produced Qty Variance (Batch Execution Acceptance Limits)
                    </div>
                    <TableContainer component={Paper} className={classes.table}>
                      <Table>
                        <colgroup>
                          <col style={{ width: "22%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "24%" }} />
                          <col style={{ width: "26%" }} />
                          <col style={{ width: "16%" }} />
                        </colgroup>
                        <TableHead className={classes.tableHead}>
                          <TableRow>
                            <TableCell className={classes.tableCell}>Product Type</TableCell>
                            <TableCell className={classes.tableCell}>Variance Limit (%) <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Update PO Qty in ERP <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Update Type <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Warning Message <span style={{ color: "red" }}>*</span></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productTypesData.filter((doc) => !!doc.activated).map((doc) => {
                            const lessUpdateTypeDisabled = !editMode || !doc.lessProducedQtyUpdateInERP;
                            return (
                              <TableRow key={doc.productTypeID}>
                                <TableCell>{doc.productTypeName}</TableCell>
                                <TableCell>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={doc.lessProducedQtyVarianceLimitPercentage ?? ""}
                                    onChange={(e) => handleMandatoryChange(doc.productTypeID, "lessProducedQtyVarianceLimitPercentage", e.target.value)}
                                    disabled={!editMode}
                                    inputProps={{ min: 0 }}
                                    onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                                    style={{ width: 90 }}
                                    error={!!tableErrors.lessProducedTable && (doc.lessProducedQtyVarianceLimitPercentage == null || String(doc.lessProducedQtyVarianceLimitPercentage).trim() === "" || isNaN(parseInt(doc.lessProducedQtyVarianceLimitPercentage)))}
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.lessProducedTable && doc.lessProducedQtyUpdateInERP == null}>
                                    <Select
                                      value={doc.lessProducedQtyUpdateInERP === true ? "Yes" : doc.lessProducedQtyUpdateInERP === false ? "No" : ""}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "lessProducedQtyUpdateInERP", e.target.value)}
                                      disabled={!editMode}
                                      displayEmpty
                                    >
                                      {doc.lessProducedQtyUpdateInERP == null && <MenuItem value="" disabled>-</MenuItem>}
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.lessProducedTable && !!doc.lessProducedQtyUpdateInERP && !doc.lessProducedQtyUpdateType}>
                                    <Select
                                      value={!doc.lessProducedQtyUpdateInERP ? "NA" : (doc.lessProducedQtyUpdateType || "")}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "lessProducedQtyUpdateType", e.target.value)}
                                      disabled={lessUpdateTypeDisabled}
                                      displayEmpty
                                    >
                                      {!doc.lessProducedQtyUpdateInERP && <MenuItem value="NA">NA</MenuItem>}
                                      <MenuItem value="Manual">Manual</MenuItem>
                                      <MenuItem value="Automatic after approval">Automatic after approval</MenuItem>
                                      <MenuItem value="Automatic without approval">Automatic without approval</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.lessProducedTable && doc.lessProducedQtyWarningMessageRequired == null}>
                                    <Select
                                      value={doc.lessProducedQtyWarningMessageRequired === true ? "Yes" : doc.lessProducedQtyWarningMessageRequired === false ? "No" : ""}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "lessProducedQtyWarningMessageRequired", e.target.value)}
                                      disabled={!editMode}
                                      displayEmpty
                                    >
                                      {doc.lessProducedQtyWarningMessageRequired == null && <MenuItem value="" disabled>-</MenuItem>}
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {tableErrors.lessProducedTable && (
                      <Typography style={{ color: "#f44336", fontSize: 14, marginTop: 8, whiteSpace: "pre-line" }}>{tableErrors.lessProducedTable}</Typography>
                    )}
                  </div>

                  {/* Table 7: More Produced Qty Variance */}
                  <div>
                    <div
                      style={{
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: 800,
                        fontSize: 18,
                        marginTop: 24,
                      }}
                    >
                      More Produced Qty Variance (Batch Execution Acceptance Limits)
                    </div>
                    <TableContainer component={Paper} className={classes.table}>
                      <Table>
                        <colgroup>
                          <col style={{ width: "22%" }} />
                          <col style={{ width: "12%" }} />
                          <col style={{ width: "24%" }} />
                          <col style={{ width: "26%" }} />
                          <col style={{ width: "16%" }} />
                        </colgroup>
                        <TableHead className={classes.tableHead}>
                          <TableRow>
                            <TableCell className={classes.tableCell}>Product Type</TableCell>
                            <TableCell className={classes.tableCell}>Variance Limit (%) <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Update PO Qty in ERP <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Update Type <span style={{ color: "red" }}>*</span></TableCell>
                            <TableCell className={classes.tableCell}>Warning Message <span style={{ color: "red" }}>*</span></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productTypesData.filter((doc) => !!doc.activated).map((doc) => {
                            const moreUpdateTypeDisabled = !editMode || !doc.moreProducedQtyUpdateInERP;
                            return (
                              <TableRow key={doc.productTypeID}>
                                <TableCell>{doc.productTypeName}</TableCell>
                                <TableCell>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    type="number"
                                    value={doc.moreProducedQtyVarianceLimitPercentage ?? ""}
                                    onChange={(e) => handleMandatoryChange(doc.productTypeID, "moreProducedQtyVarianceLimitPercentage", e.target.value)}
                                    disabled={!editMode}
                                    inputProps={{ min: 0 }}
                                    onKeyDown={(e) => ['e', 'E', '+', '-', '.'].includes(e.key) && e.preventDefault()}
                                    style={{ width: 90 }}
                                    error={!!tableErrors.moreProducedTable && (doc.moreProducedQtyVarianceLimitPercentage == null || String(doc.moreProducedQtyVarianceLimitPercentage).trim() === "" || isNaN(parseInt(doc.moreProducedQtyVarianceLimitPercentage)))}
                                  />
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.moreProducedTable && doc.moreProducedQtyUpdateInERP == null}>
                                    <Select
                                      value={doc.moreProducedQtyUpdateInERP === true ? "Yes" : doc.moreProducedQtyUpdateInERP === false ? "No" : ""}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "moreProducedQtyUpdateInERP", e.target.value)}
                                      disabled={!editMode}
                                      displayEmpty
                                    >
                                      {doc.moreProducedQtyUpdateInERP == null && <MenuItem value="" disabled>-</MenuItem>}
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.moreProducedTable && !!doc.moreProducedQtyUpdateInERP && !doc.moreProducedQtyUpdateType}>
                                    <Select
                                      value={!doc.moreProducedQtyUpdateInERP ? "NA" : (doc.moreProducedQtyUpdateType || "")}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "moreProducedQtyUpdateType", e.target.value)}
                                      disabled={moreUpdateTypeDisabled}
                                      displayEmpty
                                    >
                                      {!doc.moreProducedQtyUpdateInERP && <MenuItem value="NA">NA</MenuItem>}
                                      <MenuItem value="Manual">Manual</MenuItem>
                                      <MenuItem value="Automatic after approval">Automatic after approval</MenuItem>
                                      <MenuItem value="Automatic without approval">Automatic without approval</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>
                                  <FormControl variant="outlined" size="small" className={classes.mandatoryDropdown} error={!!tableErrors.moreProducedTable && doc.moreProducedQtyWarningMessageRequired == null}>
                                    <Select
                                      value={doc.moreProducedQtyWarningMessageRequired === true ? "Yes" : doc.moreProducedQtyWarningMessageRequired === false ? "No" : ""}
                                      onChange={(e) => handleMandatoryChange(doc.productTypeID, "moreProducedQtyWarningMessageRequired", e.target.value)}
                                      disabled={!editMode}
                                      displayEmpty
                                    >
                                      {doc.moreProducedQtyWarningMessageRequired == null && <MenuItem value="" disabled>-</MenuItem>}
                                      <MenuItem value="No">No</MenuItem>
                                      <MenuItem value="Yes">Yes</MenuItem>
                                    </Select>
                                  </FormControl>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {tableErrors.moreProducedTable && (
                      <Typography style={{ color: "#f44336", fontSize: 14, marginTop: 8, whiteSpace: "pre-line" }}>{tableErrors.moreProducedTable}</Typography>
                    )}
                  </div>
                </>
              )}
            </form>
            <Divider
              style={{
                margin: "80px 00px 0px 00px",
                backgroundColor: "#707070",
                height: 1,
              }}
            />
          </div>
        </div>
      </>
    );
  },
);

export default ProductsTypesSection;
